import * as actions from '../../actions';
import {call, put, select, takeLatest} from 'redux-saga/effects';
import {ActionType} from 'typesafe-actions';
import {showToast} from '../../../utils/showToast';
import {AxiosError, AxiosResponse} from 'axios';
import JSONFormData from '../../../utils/JSONFormData';
import {api} from '../../services/axios';
import {
    AddOptionsTemplateResponse,
    AddUAPayload,
    AssignedPortInfo,
    CreateDevicePhoneBookResponse,
    GetPhoneBoolListResponse,
    GetUAListResponse,
    GetUAProfileMetaInfoResponse,
    ListFileFormat,
    OptionsTemplateInfo,
    OptionsTemplateListPayload,
    PhoneDirectory,
    ReleaseDevicePayload,
    UAInfo,
    UATypeInfo,
    UATypeListPayload,
} from '../../types/Devices';
import {ExtensionsListItem} from '../../reducers/extensions/extensions/reducer';
import {Account, Customer, Generic, UA} from '../../../services/endpoints';
import {APIErrorInterface} from '../../types';
import fileDownload from 'js-file-download';
import {pagedDataRequest} from '../paged.data.saga';
import { YesNo } from '../../types/CallScreening';

export const DEVICE_PROFILE_NOT_FOUND_API_ERROR = 'DEVICE_PROFILE_NOT_FOUND_API_ERROR';
export const DEVICE_PHONE_BOOK_NOT_FOUND_API_ERROR = 'Server.UA.i_ua_config_directory.not_found';
export const DEVICE_PROFILE_IS_NOT_UNIQUE_API_ERROR = 'Client.UA.config_template.name_in_use';

export function* getDeviceList(
    action: ActionType<typeof actions.devicesList.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    const {ua_type_list} = yield select((state) => state.devices);
    const {i_customer} = yield select((state) => state.generic.sessionData);

    const body = new JSONFormData(session_id, csrf_token);
    body.setParams(action.payload);

    try {
        if (ua_type_list == undefined && i_customer) {
            yield call(getDeviceTypeList,
                actions.deviceTypeList.request({
                    get_total: 1,
                    i_customer: i_customer
                }));
        }

        const res: AxiosResponse<GetUAListResponse> = yield api.post(
            UA.GetUaList,
            body,
        );

        const useageOption = ['U', 'C'];

        body.setParams({
            ...action.payload,
            usage: action.payload.usage != undefined
            && useageOption.indexOf(action.payload.usage) != -1 ? action.payload.usage : 'U',
            get_total: 1,
            limit: 1,
        });

        const active: AxiosResponse<GetUAListResponse> = yield api.post(
            UA.GetUaList,
            body,
        );

        action.payload.callback?.();

        yield put(
            actions.devicesList.success({
                items: res.data.ua_list,
                total: res.data.total,
                active: action.payload.usage == 'F' ? 0 : active.data.total,
            }),
        );
    } catch (e) {
        // @ts-ignore
        showToast(e);
        yield put(actions.devicesList.failure());
    }
}

export function* releaseDevice(
    action: ActionType<typeof actions.releaseDevice.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    const body = new JSONFormData(session_id, csrf_token);

    try {
        if (action.payload.i_ua_link) {
            body.setParams({
                i_ua_link: action.payload.i_ua_link,
            });

            yield api.post(Account.ReleaseUA, body);

            action.payload.onSuccess?.();

            if (action.payload.withAssign === true) {
            } else {
                yield put(actions.releaseDevice.success());
            }
        } else {
            body.setParams({
                extension: action.payload.extension_id,
                get_main_office_extensions: 1
            });
            const extResponse: AxiosResponse<{
                extensions_list: ExtensionsListItem[];
                total: number;
            }> = yield api.post(Customer.GetExtensionsList, body);

            const {i_c_ext} = extResponse.data.extensions_list[0];
            const payload: ReleaseDevicePayload = {
                i_c_ext: i_c_ext || 0,
                i_cpe: null,
            };

            body.setParams(payload);
            yield api.post(Customer.UpdateCustomerExtension, body);

            yield put(actions.releaseDevice.success());
            action.payload.onSuccess?.();
        }

        action.payload.callback?.();
    } catch (e) {
        action.payload.callback?.();
        // @ts-ignore
        showToast(e);
        yield put(actions.releaseDevice.failure());
    }
}

export function* fetchExtensionById(
    action: ActionType<typeof actions.getExtensionById.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    const body = new JSONFormData(session_id, csrf_token);
    body.setParams({
        extension: action.payload.id,
        detailed_info: 1,
        get_main_office_extensions: 1
    });
    try {
        const res: AxiosResponse<{
            extensions_list: ExtensionsListItem[];
            total: number;
        }> = yield api.post(Customer.GetExtensionsList, body);

        const payload = {
            extension: res.data.extensions_list.length
                ? res.data.extensions_list[0]
                : undefined,
        };

        yield put(actions.getExtensionById.success(payload));
    } catch (e) {
        // @ts-ignore
        showToast(e);
        yield put(actions.getExtensionById.failure());
    }
}

export function* assignDeviceToExtension(
    action: ActionType<typeof actions.assignDevice.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    const body = new JSONFormData(session_id, csrf_token);

    if (action.payload.withRelease && action.payload.extension_id) {
        body.setParams({
            extension: action.payload.extension_id,
            get_main_office_extensions: 1
        });

        const extResponse: AxiosResponse<{
            extensions_list: ExtensionsListItem[];
        }> = yield api.post(Customer.GetExtensionsList, body);

        body.setParams({
            i_c_ext: extResponse.data.extensions_list[0].i_c_ext,
            i_cpe: null,
        });

        yield api.post(Customer.UpdateCustomerExtension, body);
    }

    body.setParams({
        port: action.payload.port,
        i_cpe: action.payload.i_cpe,
        i_c_ext: action.payload.i_c_ext,
        i_account: action.payload.i_account ? action.payload.i_account : undefined
    });

    try {
        yield api.post('Customer/update_customer_extension', body);
        yield put(actions.assignDevice.success());
        action.payload.onSuccess?.();
    } catch (e) {
        // @ts-ignore
        if (e.response?.data) {
            // @ts-ignore
            showToast(e.response?.data.faultstring);
        } else {
            // @ts-ignore
            showToast(e);
        }

        yield put(actions.assignDevice.failure());
    }
}

export function* fetchDeviceByIAccount(
    action: ActionType<typeof actions.fetchDeviceByIAccount.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    const body = new JSONFormData(session_id, csrf_token);
    const {i_customer} = yield select((state) => state.generic.sessionData);

    try {
        body.setParams({
            i_account: action.payload.i_account,
            with_available_ports: 1
        });

        yield call(getDeviceTypeList,
            actions.deviceTypeList.request({
                get_total: 1,
                i_customer
            }));

        const items: AssignedPortInfo[] = yield pagedDataRequest<AssignedPortInfo>(Account.GetAssignedCpePortsList, action.payload, (data) => data.assigned_cpe_ports_list);

        if (action.payload.withDeviceDetails && items.length > 0) {
            const devices: UAInfo[] = yield pagedDataRequest<UAInfo>(UA.GetUaList, {}, (data) => data.ua_list);
            for (const port of items) {
                const device = devices.find(e => e.i_ua === port.i_ua);
                if (!device) continue;
                port.ua_info = device;
            }
        }

        yield put(
            actions.fetchDeviceByIAccount.success({
                items: items,
            }),
        );
    } catch (e) {
        // @ts-ignore
        showToast(e);
        yield put(actions.fetchDeviceByIAccount.failure());
    }
}

export function* getDeviceTypeList(
    action: ActionType<typeof actions.deviceTypeList.request>,
) {
    try {
        const deviceTypes: UATypeInfo[] = yield pagedDataRequest<UATypeInfo>(UA.GetUaTypeList, action.payload, (data) => data.ua_type_list);
        const response: UATypeListPayload = {
            ua_type_list: deviceTypes,
            total: deviceTypes.length
        };
        yield put(actions.deviceTypeList.success(response));
    } catch (e) {
        // @ts-ignore
        showToast(e);
        yield put(actions.devicesList.failure());
    }
}

export function* addDevice(
    action: ActionType<typeof actions.addDevice.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    const body = new JSONFormData(session_id, csrf_token);
    try {
        body.setParams({ua_info: action.payload});
        const res: AxiosResponse<AddUAPayload> = yield api.post(
            UA.AddUa,
            body,
        );
        yield put(actions.addDevice.success(res.data));
    } catch (e) {
        //@ts-ignore
        if ((e as AxiosError)?.isAxiosError) {
            const err = (e as AxiosError)?.response?.data as APIErrorInterface;
            yield put(actions.addDevice.failure(err));
        } else {
            yield put(actions.addDevice.failure(undefined));
        }
    }
}

export function* getDeviceListCsvFile(
    action: ActionType<typeof actions.getDeviceListCsvFile.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    const body = new JSONFormData(session_id, csrf_token);
    body.setParams({
        ...action.payload,
        response_format: 'file'
    });

    try {
        const res: AxiosResponse<string> = yield api.post(
            UA.GetUaList,
            body,
            {responseType: 'blob'},
        );

        fileDownload(res.data, `${action.payload.fileName}.${action.payload.file_format || ListFileFormat.CSV}`);

        if (action.payload.callback) {
            action.payload.callback();
        }

        yield put(actions.getDeviceListCsvFile.success());
    } catch (e) {
        // @ts-ignore
        showToast(e);
        yield put(actions.getDeviceListCsvFile.failure());
    }
}

export function* uploadDeviceListCsvFile(
    action: ActionType<typeof actions.uploadDeviceListCsvFile.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    const body = new JSONFormData(session_id, csrf_token);

    try {
        body.setParams({
            file_name: action.payload.name,
            response_format: 'file',
            file_format: action.payload.file_format
        });

        if (action.payload.file) {
            body.append('upload_file', action.payload.file);

            yield api.post(UA.UploadUaList, body, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

        }

        if (action.payload.callback) {
            action.payload.callback();
        }

        yield put(actions.uploadDeviceListCsvFile.success());
    } catch (e) {
        // @ts-ignore
        if ((e as AxiosError)?.isAxiosError) {
            const err = (e as AxiosError)?.response?.data as APIErrorInterface;

            if (err && action.payload.errorCallback) {
                action.payload.errorCallback(err);
            }

            yield put(actions.uploadDeviceListCsvFile.failure(err));
        } else {
            yield put(actions.uploadDeviceListCsvFile.failure(undefined));
        }
    }
}

export function* getUploadUAApiErrorFile(
    action: ActionType<typeof actions.getUploadUAApiErrorFile.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    const body = new JSONFormData(session_id, csrf_token);
    body.setParams({
        uuid: action.payload.uuid,
        keep_stored: 1
    });

    try {
        const res: AxiosResponse<string> = yield api.post(
            Generic.GetApiErrorFile,
            body,
            {responseType: 'blob'},
        );

        fileDownload(res.data, action.payload.fileName);

        yield put(actions.getUploadUAApiErrorFile.success());
    } catch (e) {
        // @ts-ignore
        if ((e as AxiosError)?.isAxiosError) {

            const data = (e as AxiosError)?.response?.data;
            if (data) {
                const isJsonBlob = data instanceof Blob && data.type === "application/json";

                //@ts-ignore
                const responseData = isJsonBlob ? (yield data?.text()) : data || {};
                const responseJson = (typeof responseData === "string") ? JSON.parse(responseData) : responseData;

                const err = responseJson as APIErrorInterface;
                if (err && action.payload.errorCallback) {
                    action.payload.errorCallback(err);
                }

                yield put(actions.getUploadUAApiErrorFile.failure(err));
            } else {
                yield put(actions.getUploadUAApiErrorFile.failure(undefined));
            }
        } else {
            yield put(actions.getUploadUAApiErrorFile.failure(undefined));
        }
    }
}

export function* deleteUA(
    action: ActionType<typeof actions.deleteUA.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    const body = new JSONFormData(session_id, csrf_token);
    body.setParams(action.payload);

    try {
        const res: AxiosResponse<{ success: number }> = yield api.post(
            UA.DeleteUA,
            body,
        );

        if (res.data.success === 1) {
            action.payload.callback?.();
            yield put(actions.deleteUA.success());
        } else {
            throw res.data;
        }
    } catch (e) {
        // @ts-ignore
        if (e.response) {
            // @ts-ignore
            showToast(e.response.data.faultstring);
        } else {
            showToast(undefined);
        }
        yield put(actions.devicesList.failure());
    }
}

export function* assignUA(
    action: ActionType<typeof actions.assignUA.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    const body = new JSONFormData(session_id, csrf_token);

    const payload = {
        cpe_ports_list: [
            {
                i_ua: action.payload.i_ua,
                port: action.payload.port,
                ports_config_list: [
                    {
                        i_service_type: 3,
                        port: action.payload.port
                    }
                ]
            }
        ],
        i_account: action.payload.i_account
    };
    body.setParams(payload);

    try {
        const res: AxiosResponse<{ success: number }> = yield api.post(
            Account.AssignUA,
            body,
        );

        if (res.data.success === 1) {
            action.payload.callback?.();
            action.payload.onSuccess?.();
            yield put(actions.assignUA.success());
        } else {
            throw res.data;
        }
    } catch (e) {
        action.payload.callback?.();
        // @ts-ignore
        if (e?.response) {
            // @ts-ignore
            showToast(e.response.data.faultstring);
        } else {
            showToast(undefined);
        }
        const err = (e as AxiosError)?.response?.data as APIErrorInterface;
        yield put(actions.assignUA.failure(err));
    }
}

export function* updateUa(
    action: ActionType<typeof actions.updateUa.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    const body = new JSONFormData(session_id, csrf_token);
    try {
        body.setParams({ua_info: action.payload.object});

        yield api.post(
            UA.UpdateUa,
            body,
        );
        if (action.payload.callback) {
            action.payload.callback();
        }
        yield put(actions.updateUa.success());
    } catch (e) {
        action.payload.callbackOnError?.();
        //@ts-ignore
        if ((e as AxiosError)?.isAxiosError) {
            const err = (e as AxiosError)?.response?.data as APIErrorInterface;
            yield put(actions.updateUa.failure(err));
        } else {
            yield put(actions.updateUa.failure(undefined));
        }
    }
}

export function* getOptionsTemplateList(
    action: ActionType<typeof actions.getOptionsTemplateList.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);

    const body = new JSONFormData(session_id, csrf_token);
    body.setParams(action.payload);

    try {
        const res: AxiosResponse<OptionsTemplateListPayload> = yield api.post(
            UA.GetOptionsTemplateList,
            body,
        );

        yield put(
            actions.getOptionsTemplateList.success(res.data),
        );
    } catch (e) {
        //@ts-ignore
        if ((e as AxiosError)?.isAxiosError) {
            const err = (e as AxiosError)?.response?.data as APIErrorInterface;
            yield put(actions.getOptionsTemplateList.failure(err));
        } else {
            yield put(actions.getOptionsTemplateList.failure(undefined));
        }
    }
}

export function* updateOptionsTemplate(
    action: ActionType<typeof actions.updateOptionsTemplate.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);

    const body = new JSONFormData(session_id, csrf_token);
    body.setParams(action.payload);

    try {

        for(let indx = 0; indx < action.payload.length; indx++) {

            const obj = action.payload[indx];
            body.setParams({
                ...obj,
                modelName: undefined
            });

            yield api.post(
                UA.UpdateOptionsTemplate,
                body,
            );

            const nextObj = indx + 1 !== (action.payload?.length ?? 0)
                ? action.payload[indx + 1]
                : undefined;

            yield put(
                actions.updateOptionsTemplate.success({
                    done: indx + 1,
                    total: action.payload?.length,
                    profileName: nextObj?.modelName,
                    template_info: obj.template_info
                }),
            );
        }

        const callback = action.payload?.find(e => !!e.callback)?.callback;
        callback?.();
    } catch (e) {
        const errorCallback = action.payload?.find(e => !!e.callback)?.errorCallback;
        //@ts-ignore
        if ((e as AxiosError)?.isAxiosError) {
            const err = (e as AxiosError)?.response?.data as APIErrorInterface;
            if(errorCallback) {
                errorCallback?.(err);
            }
            else if (err.faultcode !== DEVICE_PROFILE_IS_NOT_UNIQUE_API_ERROR) {
                showToast(err.faultstring);
            }
            yield put(actions.updateOptionsTemplate.failure(err));
        } else {
            yield put(actions.updateOptionsTemplate.failure(undefined));
        }
    }
}

export function* addDeviceProfile(
    action: ActionType<typeof actions.addDeviceProfile.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    const body = new JSONFormData(session_id, csrf_token);
    try {
        body.setParams(action.payload);
        const res: AxiosResponse<AddOptionsTemplateResponse> = yield api.post(
            UA.AddOptionsTemplate,
            body,
        );
        action.payload.callback?.(res.data.i_ua_config_template);
        yield put(actions.addDeviceProfile.success(res.data));
    } catch (e) {
        const errorCallback = action.payload?.errorCallback;
        //@ts-ignore
        if ((e as AxiosError)?.isAxiosError) {
            const err = (e as AxiosError)?.response?.data as APIErrorInterface;
            if(errorCallback) {
                errorCallback?.(err);
            }
            else if(err.faultcode !== 'Client.UA.invalid_template.wrong_i_ua_type'){
                showToast(err.faultstring);
            }
            yield put(actions.addDeviceProfile.failure(err));
        } else {
            yield put(actions.addDeviceProfile.failure(undefined));
        }
    }
}

export function* deleteProfile(
    action: ActionType<typeof actions.deleteProfile.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    const body = new JSONFormData(session_id, csrf_token);
    body.setParams(action.payload);

    try {
        yield api.post(
            UA.DeleteProfile,
            body,
        );

        action.payload.callback?.();
        yield put(actions.deleteProfile.success());
    } catch (e) {
        //@ts-ignore
        if ((e as AxiosError)?.isAxiosError) {
            const err = (e as AxiosError)?.response?.data as APIErrorInterface;
            yield put(actions.deleteProfile.failure(err));
        } else {
            yield put(actions.deleteProfile.failure(undefined));
        }
    }
}

export function* getProfileTemplate(
    action: ActionType<typeof actions.getProfileTemplate.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    const body = new JSONFormData(session_id, csrf_token);
    body.setParams(action.payload);

    try {
        const res: AxiosResponse<{
            template_info: OptionsTemplateInfo
        }> = yield api.post(
            UA.GetProfileDetails,
            body,
        );

        if(!res.data?.template_info) {
            const err: APIErrorInterface = {
                faultcode: DEVICE_PROFILE_NOT_FOUND_API_ERROR,
                faultstring: DEVICE_PROFILE_NOT_FOUND_API_ERROR
            };
            yield put(actions.getProfileTemplate.failure(err));
            return;
        }

        yield put(actions.getProfileTemplate.success(res.data?.template_info));
    } catch (e) {
        //@ts-ignore
        if ((e as AxiosError)?.isAxiosError) {
            const err = (e as AxiosError)?.response?.data as APIErrorInterface;
            yield put(actions.getProfileTemplate.failure(err));
        } else {
            yield put(actions.getProfileTemplate.failure(undefined));
        }
    }
}

export function* getUaProfileMetaInfo(
    action: ActionType<typeof actions.getUaProfileMetaInfo.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    const body = new JSONFormData(session_id, csrf_token);
    body.setParams(action.payload);

    try {
        const res: AxiosResponse<GetUAProfileMetaInfoResponse> = yield api.post(
            UA.GetProfileMetaInfo,
            body,
        );

        yield put(actions.getUaProfileMetaInfo.success(res.data));
    } catch (e) {
        //@ts-ignore
        if ((e as AxiosError)?.isAxiosError) {
            const err = (e as AxiosError)?.response?.data as APIErrorInterface;
            yield put(actions.getUaProfileMetaInfo.failure(err));
        } else {
            yield put(actions.getUaProfileMetaInfo.failure(undefined));
        }
    }
}

export function* getDevicePhoneBookList(
    action: ActionType<typeof actions.getDevicePhoneBookList.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);

    const body = new JSONFormData(session_id, csrf_token);
    body.setParams(action.payload);

    try {
        const res: AxiosResponse<GetPhoneBoolListResponse> = yield api.post(
            UA.GetPhoneDirectoryList,
            body,
        );

        yield put(
            actions.getDevicePhoneBookList.success(res.data),
        );
    } catch (e) {
        //@ts-ignore
        if ((e as AxiosError)?.isAxiosError) {
            const err = (e as AxiosError)?.response?.data as APIErrorInterface;
            yield put(actions.getDevicePhoneBookList.failure(err));
        } else {
            yield put(actions.getDevicePhoneBookList.failure(undefined));
        }
    }
}

export function* addDevicePhoneBook(
    action: ActionType<typeof actions.addDevicePhoneBook.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    const body = new JSONFormData(session_id, csrf_token);
    try {
        body.setParams(action.payload);
        const res: AxiosResponse<CreateDevicePhoneBookResponse> = yield api.post(
            UA.AddPhoneBook,
            body,
        );

        action.payload.callback?.(res.data.i_ua_config_directory);
        yield put(actions.addDevicePhoneBook.success(res.data));
    } catch (e) {
        //@ts-ignore
        if ((e as AxiosError)?.isAxiosError) {
            const err = (e as AxiosError)?.response?.data as APIErrorInterface;
            yield put(actions.addDevicePhoneBook.failure(err));
        } else {
            yield put(actions.addDevicePhoneBook.failure(undefined));
        }
    }
}

export function* deleteDevicePhoneBook(
    action: ActionType<typeof actions.deleteDevicePhoneBook.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    const body = new JSONFormData(session_id, csrf_token);
    body.setParams(action.payload);

    try {
        yield api.post(
            UA.DeletePhoneBook,
            body,
        );

        action.payload.callback?.();
        yield put(actions.deleteDevicePhoneBook.success());
    } catch (e) {
        //@ts-ignore
        if ((e as AxiosError)?.isAxiosError) {
            const err = (e as AxiosError)?.response?.data as APIErrorInterface;
            action.payload.errorCallback?.(err);
            yield put(actions.deleteDevicePhoneBook.failure(err));
        } else {
            yield put(actions.deleteDevicePhoneBook.failure(undefined));
        }
    }
}

export function* uploadDevicePhoneBookCsvFile(
    action: ActionType<typeof actions.uploadDevicePhoneBookCsvFile.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    const body = new JSONFormData(session_id, csrf_token);

    try {
        body.setParams({
            i_ua_config_directory: action.payload.i_ua_config_directory
        });

        if (action.payload.file) {
            body.append('upload_file', action.payload.file);

            yield api.post(UA.UploadDevicePhoneBookCsv, body, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
        }

        if (action.payload.callback) {
            action.payload.callback();
        }

        yield put(actions.uploadDevicePhoneBookCsvFile.success());
    } catch (e) {
        // @ts-ignore
        if ((e as AxiosError)?.isAxiosError) {
            const err = (e as AxiosError)?.response?.data as APIErrorInterface;

            if (err && action.payload.errorCallback) {
                action.payload.errorCallback(err);
            }

            yield put(actions.uploadDevicePhoneBookCsvFile.failure(err));
        } else {
            yield put(actions.uploadDevicePhoneBookCsvFile.failure(undefined));
        }
    }
}

export function* getPhoneBookDetails(
    action: ActionType<typeof actions.getPhoneBookDetails.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    const body = new JSONFormData(session_id, csrf_token);
    body.setParams(action.payload);

    try {
        if(action.payload.response_format.return_in_file === YesNo.Yes) {
            const res: AxiosResponse<string> = yield api.post(
                UA.GetDevicePhoneBookDetails,
                body,
            );
            fileDownload(res.data, `${action.payload.fileName ?? 'entries'}.${action.payload.response_format.file_format}`);
        }
        else
        {
            const res: AxiosResponse<{
                phone_directory_info: PhoneDirectory
            }> = yield api.post(
                UA.GetDevicePhoneBookDetails,
                body,
            );

            yield put(actions.getPhoneBookDetails.success(res.data?.phone_directory_info));
        }
    } catch (e) {
        //@ts-ignore
        if ((e as AxiosError)?.isAxiosError) {
            const err = (e as AxiosError)?.response?.data as APIErrorInterface;
            yield put(actions.getPhoneBookDetails.failure(err));
        } else {
            yield put(actions.getPhoneBookDetails.failure(undefined));
        }
    }
}

export function* updatePhoneBookDetails(
    action: ActionType<typeof actions.updatePhoneBookDetails.request>,
) {
    const {session_id, csrf_token} = yield select((state) => state.auth);
    
    const {phoneBookDetails} = yield select((state) => state.devices);

    const body = new JSONFormData(session_id, csrf_token);

    try {
        const mappedObject = {
            phone_directory_info: 
            { 
                ...action.payload,
                directory_records: action.payload.directory_records?.map(e => {
                    return {
                        ...e,
                        last_name: ((e.last_name?.length ?? 0) > 0) ? e.last_name : null,
                        mobile_number: ((e.mobile_number?.length ?? 0) > 0) ? e.mobile_number : null,
                        office_number: ((e.office_number?.length ?? 0) > 0) ? e.office_number : null,
                        other_number: ((e.other_number?.length ?? 0) > 0) ? e.other_number : null,
                    };
                })
            }
        };
        
        const shouldSetDraftStatus = phoneBookDetails?.state === 'active' && 
            action.payload.state === 'active';
        if(shouldSetDraftStatus)
        {
            mappedObject.phone_directory_info.state = 'draft';
        }

        body.setParams(mappedObject);

        yield api.post(
            UA.UpdatePhoneBookDetails,
            body,
        );

        yield put(actions.updatePhoneBookDetails.success({
            ...action.payload,
            state: mappedObject.phone_directory_info.state
        }));

        action.payload?.callback?.();
    } catch (e) {
        //@ts-ignore
        if ((e as AxiosError)?.isAxiosError) {
            const err = (e as AxiosError)?.response?.data as APIErrorInterface;
            action.payload.errorCallback?.(err);
            yield put(actions.updatePhoneBookDetails.failure(err));
        } else {
            yield put(actions.updatePhoneBookDetails.failure(undefined));
        }
    }
}
export const devicesSaga = [
    takeLatest(actions.devicesList.request, getDeviceList),
    takeLatest(actions.releaseDevice.request, releaseDevice),
    takeLatest(actions.getExtensionById.request, fetchExtensionById),
    takeLatest(actions.assignDevice.request, assignDeviceToExtension),
    takeLatest(actions.fetchDeviceByIAccount.request, fetchDeviceByIAccount),
    takeLatest(actions.deviceTypeList.request, getDeviceTypeList),
    takeLatest(actions.addDevice.request, addDevice),
    takeLatest(actions.getDeviceListCsvFile.request, getDeviceListCsvFile),
    takeLatest(actions.uploadDeviceListCsvFile.request, uploadDeviceListCsvFile),
    takeLatest(actions.getUploadUAApiErrorFile.request, getUploadUAApiErrorFile),
    takeLatest(actions.deleteUA.request, deleteUA),
    takeLatest(actions.updateUa.request, updateUa),
    takeLatest(actions.assignUA.request, assignUA),
    takeLatest(actions.getOptionsTemplateList.request, getOptionsTemplateList),
    takeLatest(actions.updateOptionsTemplate.request, updateOptionsTemplate),
    takeLatest(actions.addDeviceProfile.request, addDeviceProfile),
    takeLatest(actions.deleteProfile.request, deleteProfile),
    takeLatest(actions.getProfileTemplate.request, getProfileTemplate),
    takeLatest(actions.getUaProfileMetaInfo.request, getUaProfileMetaInfo),
    takeLatest(actions.getDevicePhoneBookList.request, getDevicePhoneBookList),
    takeLatest(actions.addDevicePhoneBook.request, addDevicePhoneBook),
    takeLatest(actions.deleteDevicePhoneBook.request, deleteDevicePhoneBook),
    takeLatest(actions.uploadDevicePhoneBookCsvFile.request, uploadDevicePhoneBookCsvFile),
    takeLatest(actions.getPhoneBookDetails.request, getPhoneBookDetails),
    takeLatest(actions.updatePhoneBookDetails.request, updatePhoneBookDetails),
];
